import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { getCookie } from '@directv-digital/dds.services.cookie-service';
import { fireComponentDisplayedEvent } from "@directv-digital/dds.services.analytics-service";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import { logGenericEvent } from 'lib/helper/logger/logger';
import { getTrafficGroup } from 'src/utils/trafficConfigurator';
import { isEmptyObj } from 'src/utils/UtilityFunctions';
import PurchaseLikelihoodModal from './purchase-likelihood-modal';

export interface ModalContent {
  cta?: {
    label: string;
    disabled: boolean;
    targeturl: string;
    accessibility: string;
  };
  image?: {
    alt: string;
    url: string;
    _uid:string;
    isImage: boolean;
    hasSound: boolean;
  };
  title?: string;
  subtitle?: string;
  signUpCta?: {
    label: string;
    disabled: boolean;
    targeturl: string;
    accessibility: string;
  };
  emailCapture?:any,
  emailLabel?: string;
  mainContent?: string;
  optionalCta?: {
    label: string;
    disabled: boolean;
    targeturl: string;
    accessibility: string;
  };
  emailErrorMessage?: string;
  trafficDistributionPercentage?: number | string;
}
/**
 * `genre`/`signature` are legacy OTF-only variants (pre-dating generic segment
 * keys) — kept so existing OTF CMS content configured either way still resolves.
 * Any other segment key reads `content` directly.
 */
export interface ModalDataEntry {
  content?: ModalContent;
  genre?: { content?: ModalContent };
  signature?: { content?: ModalContent };
}
/**
 * Keyed by Session AI segment — each key must match a `capturedSegment.eppLabel`
 * value (e.g. `HLB`, `ULB`, `OTF`) for its content to resolve. Not limited to
 * those three: any CMS-configured segment key works via the generic lookup in
 * `getEntryContent`/`getConfiguredSegments` below.
 */
export interface ModalData {
  [segmentKey: string]: ModalDataEntry | undefined;
}
export interface MarketingAiModalProps {
  id?: string;
  type?: string;
  modalData?: ModalData;
  /**
   * WAT-10279: Captured Session AI segment from the orchestrator. The
   * orchestrator owns the z1_analytic listener so the buffer is alive
   * BEFORE this modal mounts (covers the priority-fallthrough case).
   * - null: not yet captured. Modal waits with timeout (in priority mode)
   *   or waits indefinitely (in non-priority mode).
   * - object: segment is known; modal renders if content matches.
   */
  capturedSegment?: { eppLabel: string; source: 'realtime' | 'localstorage' } | null;
  /** WAT-10279: Callback to signal the orchestrator this modal won't display */
  onSkip?: () => void;
  /**
   * WAT-10279: Max time (ms) to wait for a segment before giving up and
   * calling `onSkip` so the orchestrator can fall through to the next
   * priority modal. Default 30s — z1 fires ~12s after threshold-hitting
   * page load, so 30s is a comfortable buffer.
   */
  segmentWaitTimeoutMs?: number;
};

const LOG_NAME = 'SESSIONAIMODAL';
const DEFAULT_SEGMENT_WAIT_TIMEOUT_MS = 30000;

const getPage = (): string => (typeof window !== 'undefined' ? window.location.pathname : 'ssr');

const getEntryContent = (entry?: ModalDataEntry): ModalContent | null => entry?.content || entry?.genre?.content || entry?.signature?.content || null;

const getConfiguredSegments = (modalData?: ModalData): string[] => {
  if (!modalData) {
    return [];
  }
  return Object.keys(modalData).filter(key => !!getEntryContent(modalData[key]));
};

/**
 * Checks if the user is authenticated or recognized based on specific cookies.
 */
const isUserAuthenticatedOrRecognized = (): boolean => {
  const cAuthNStateCookie = getCookie("cAuthNState");
  const recogmeCookie = getCookie("recogme");
  const accTypeInCtxCookie = getCookie("accTypeInCtx");
  const isAuthenticated = (cAuthNStateCookie && cAuthNStateCookie !== 'en') && typeof accTypeInCtxCookie !== 'undefined';
  const isRecognized = !!recogmeCookie && recogmeCookie !== '' && !!accTypeInCtxCookie && accTypeInCtxCookie !== '';
  return isAuthenticated || isRecognized;
};

export const MarketingAiModal = (props: MarketingAiModalProps): JSX.Element => {
  try {
    const [dismissed, setDismissed] = useState(false);
    const analyticsFiredRef = useRef(false);
    const {
      modalData,
      onSkip,
      capturedSegment,
      segmentWaitTimeoutMs = DEFAULT_SEGMENT_WAIT_TIMEOUT_MS
    } = props;

    // Segment now flows in via prop from the orchestrator (single owner of the
    // z1_analytic listener + GA dataLayer push). MAI doesn't subscribe directly.
    const { eppLabel: segment = null, source: segmentSource = null } = capturedSegment ?? {};

    // Computed once on mount — cookies don't change during lifecycle
    const isExistingUser = useMemo(() => isUserAuthenticatedOrRecognized(), []);

    /**
   * WAT-10279: React to capturedSegment + early-skip gates.
   * - Existing/recognized user → skip
   * - Already shown this session → skip
   * - capturedSegment present → continue to render path (handled by modalContent useMemo)
   * - capturedSegment null + onSkip provided (priority mode) → start timeout
   * - capturedSegment null + no onSkip (non-priority) → wait indefinitely
   * Re-runs when capturedSegment changes so a late capture cancels the timeout.
   */
    useEffect(() => {
      const page = getPage();
      const configuredSegments = getConfiguredSegments(modalData);

      logGenericEvent(LOG_NAME, 'page_configured', {
        page,
        configuredSegments,
        userType: isExistingUser ? 'existing' : 'new',
        hasCapturedSegment: !!capturedSegment
      });

      if (isExistingUser) {
        logGenericEvent(LOG_NAME, 'skipped_existing_user', { page });
        onSkip?.();
        return undefined;
      }

      const modalShown = sessionStorage.getItem('purchaseLikelihoodModalShown') === 'true';
      if (modalShown) {
        logGenericEvent(LOG_NAME, 'skipped_already_shown', { page });
        onSkip?.();
        return undefined;
      }

      // Segment already captured — render path takes over (no timeout needed).
      if (capturedSegment) {
        logGenericEvent(LOG_NAME, 'segment_received', {
          page,
          segment: capturedSegment.eppLabel,
          source: capturedSegment.source
        });
        return undefined;
      }

      // Segment not yet captured. In priority mode, start a fallback timeout
      // so the orchestrator's slot isn't held forever if the event never fires.
      // Non-priority mode: no slot to free → wait indefinitely (matches master).
      if (!onSkip) {
        return undefined;
      }

      const timeoutId = setTimeout(() => {
        logGenericEvent(LOG_NAME, 'skipped_segment_timeout', {
          page,
          waitMs: segmentWaitTimeoutMs
        });
        onSkip();
      }, segmentWaitTimeoutMs);

      return () => clearTimeout(timeoutId);
    }, [capturedSegment, isExistingUser, onSkip, segmentWaitTimeoutMs, modalData]);

    /**
   * Derive modal content from segment + CMS data (pure computation, no effect needed).
   */
    const modalContent = useMemo<ModalContent | null>(() => {
      if (!segment || !modalData) {
        return null;
      }

      return getEntryContent(modalData[segment]);
    }, [segment, modalData]);

    // Derive traffic group from content (pure computation)
    const trafficGroup = useMemo(() => {
      if (!modalContent || isEmptyObj(modalContent)) {
        return null;
      }

      return getTrafficGroup({
        trafficDistributionPercentage: modalContent.trafficDistributionPercentage || 100,
        groupKey: 'showSessionAIModalGroup',
        distributionKey: 'showSessionAIModalDistribution'
      });
    }, [modalContent]);

    // Derive open state instead of storing it
    // WAT-10279: Check shared session fatigue counter (cross-modal coordination)
    // Read sessionStorage once via useMemo to avoid per-render storage reads (js-cache-storage)
    const sessionFatigueReached = useMemo(() => {
      if (typeof window === 'undefined') {
        return false;
      }
      return parseInt(sessionStorage.getItem('modalDisplayCount') || '0', 10) >= 2;
    }, []);
    const open = !isExistingUser && !dismissed && !!modalContent && trafficGroup === 'A' && !sessionFatigueReached;

    /**
   * Handler to close the modal.
   */
    const handleModalClose = useCallback(() => {
      logGenericEvent(LOG_NAME, 'modal_closed', {
        page: getPage(),
        segment,
        source: segmentSource
      });
      setDismissed(true);
      if (typeof window !== "undefined") {
        sessionStorage.setItem("purchaseLikelihoodModalShown", 'true');
      }
    }, [segment, segmentSource]);

    /**
   * Effect 2: Dynatrace dashboard logging + GA analytics + sessionStorage flag.
   * Single effect for all side effects once the modal decision is final.
   */
    useEffect(() => {
      if (!segment || isExistingUser) {
        return;
      }

      // Log when segment doesn't match any configured content on this page
      if (!modalContent) {
        logGenericEvent(LOG_NAME, 'Segment received from SESSIONAI - segment_content_not_configured_in_CMS', {
          page: getPage(),
          receivedSegment: segment,
          configuredSegments: getConfiguredSegments(modalData)
        });
        onSkip?.(); // WAT-10279: No matching content — fall through to next priority
        return;
      }

      if (trafficGroup === null) {
        return;
      }
      // Gate analytics to once per user session — persists across reloads within the same tab.
      if (sessionStorage.getItem('purchaseLikelihoodModalAnalyticsFired') === 'true') {
        return;
      }
      if (analyticsFiredRef.current) {
        return;
      }

      const displayed = trafficGroup === 'A';

      // WAT-10279: If not displayed due to traffic group, signal orchestrator to fall through
      if (!displayed) {
        onSkip?.();
      }
      const page = getPage();
      const trafficPercentage = modalContent.trafficDistributionPercentage ?? 100;

      // Mark shown in sessionStorage so it doesn't reappear on navigation
      if (displayed && typeof window !== "undefined") {
        sessionStorage.setItem("purchaseLikelihoodModalShown", 'true');
        // WAT-10279: Increment shared modal fatigue counter for cross-modal coordination
        const currentCount = parseInt(sessionStorage.getItem('modalDisplayCount') || '0', 10);
        sessionStorage.setItem('modalDisplayCount', (currentCount + 1).toString());
      }

      // Dynatrace: Modal outcome per page
      logGenericEvent(LOG_NAME, 'modal_outcome', {
        page,
        segment,
        source: segmentSource,
        trafficGroup,
        trafficPercentage,
        displayed
      });

      // GA: Fire analytics with source for both displayed and not displayed
      const dimValue = segmentSource ?
      `marketing_ai_modal-${segmentSource}` :
        'marketing_ai_modal';

      const eventData = {
        event: 'trackEvent',
        event_name: 'component_displayed',
        dim_name: displayed ? 'component displayed' : 'component not displayed',
        dim_value: dimValue,
        splitReleaseID: `${segment}`
      };

      try {
        fireComponentDisplayedEvent(eventData);
        analyticsFiredRef.current = true;
        sessionStorage.setItem('purchaseLikelihoodModalAnalyticsFired', 'true');
        logGenericEvent(LOG_NAME, 'analytics_fired', {
          page,
          segment,
          source: segmentSource,
          trafficGroup,
          displayed,
          dimValue
        });
      } catch (error) {
        logGenericEvent(LOG_NAME, 'analytics_fire_error', {
          page,
          segment,
          source: segmentSource,
          trafficGroup,
          displayed,
          dimValue,
          error
        });
      }
    }, [modalContent, trafficGroup, segment, isExistingUser, segmentSource, modalData, onSkip]);

    // --- Render Logic ---
    return open ? (
    <PurchaseLikelihoodModal
      content={modalContent}
      modalOpen={open}
      onClose={handleModalClose}
    />
    ) : null;
  } catch (error) {
    logGenericEvent(LOG_NAME, 'render_error', {
      page: getPage(),
      error
    });
    return <ErrorDisplay component="MarketingAiModal" error={error} />;
  }
}

export default MarketingAiModal;
